// Root route — global outlet + auth-aware redirect.
//
// `beforeLoad` redirects to `/auth/login` ONLY on a *definitive* 401 from
// the `/auth/me` probe. A transient fault (5xx, dropped/aborted fetch, worker
// cold-start) fails open so navigation proceeds — otherwise a momentary blip
// would log a valid user out. The `useAuthGuard()` hook in `src/lib/auth.ts`
// covers the in-session expiry case via a `useMe()` listener mounted by the
// `_authed` layout.
//
// The auth probe goes through the same fetch wrapper our hooks use so
// cookies + correlation id ride along consistently.

import { useEffect, useRef } from 'react';
import {
  Outlet,
  createRootRouteWithContext,
  redirect,
  useRouterState,
} from '@tanstack/react-router';

import { ApiError, type ApiClient } from '@barel/api-client';
import { useT } from '@barel/i18n';

import { SkipLink, APP_MAIN_LANDMARK_ID } from '@/features/shell/SkipLink';

/**
 * Router context surfaced by `App.tsx`.
 *
 * `apiClient` is the typed Hono client (kept for parity with the rest of the
 * code). `authFetch` is the wrapped fetch + base URL bound by the App; we
 * thread it explicitly so `beforeLoad` doesn't depend on the api-client
 * WeakMap surviving Vite's module resolution.
 */
export interface RouterContext {
  apiClient: ApiClient;
  authFetch: (path: string, init?: RequestInit) => Promise<Response>;
}

interface MeResponse {
  user: { id: string };
}

/**
 * Outcome of the `/auth/me` probe:
 *  - `authenticated`   — 200 with a user id.
 *  - `unauthenticated` — a *definitive* 401 (genuinely signed out).
 *  - `indeterminate`   — a transient fault (5xx, a refused/aborted fetch, a
 *    worker cold-start, a parse failure). We must NOT log the user out on
 *    these; the navigation proceeds and the in-session `useAuthGuard`
 *    listener handles a real expiry on the next request.
 */
export type ProbeResult = 'authenticated' | 'unauthenticated' | 'indeterminate';

const PROBE_ATTEMPTS = 2;
const PROBE_RETRY_DELAY_MS = 300;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Probe `/auth/me` and classify the session. The critical distinction is
 * between a *definitive* 401 (→ redirect to login) and a *transient* fault
 * (→ fail open, never logout). Transient faults are retried briefly so a
 * one-off blip self-heals before we render through.
 */
export async function probeAuthenticated(
  authFetch: RouterContext['authFetch'],
): Promise<ProbeResult> {
  for (let attempt = 0; attempt < PROBE_ATTEMPTS; attempt += 1) {
    try {
      const response = await authFetch('/api/v1/auth/me', { method: 'GET' });
      // Only a 401 is an authoritative "signed out" verdict. A 403
      // (e.g. email-not-verified) means the user *is* logged in, so it must
      // not bounce them to login — treat anything non-401 / non-2xx as
      // transient and retry.
      if (response.status === 401) return 'unauthenticated';
      if (response.ok) {
        const body = (await response.json()) as MeResponse;
        if (body?.user?.id) return 'authenticated';
      }
    } catch {
      // Network-level failure (refused / aborted / offline) — transient.
    }
    if (attempt < PROBE_ATTEMPTS - 1) await delay(PROBE_RETRY_DELAY_MS);
  }
  // Exhausted retries without a definitive verdict — fail open.
  return 'indeterminate';
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context, location }) => {
    const path = location.pathname;
    const isAuthRoute = path.startsWith('/auth');
    if (isAuthRoute) {
      // Auth pages are always reachable; they perform their own redirects
      // on success via the api-client hooks.
      return;
    }
    // Redirect ONLY on a definitive 401. A transient probe failure resolves
    // to `indeterminate` → fail open (navigation proceeds); `useAuthGuard`
    // catches a real expiry on the next request. This prevents a 5xx / worker
    // cold-start / dropped fetch during navigation from logging the user out.
    const result = await probeAuthenticated(context.authFetch);
    if (result === 'unauthenticated') {
      throw redirect({
        to: '/auth/login',
        search: { from: path },
      });
    }
  },
  // Render the route tree below; the per-segment layouts mount the actual
  // chrome (auth split-screen vs `_authed` shell). We mount a single global
  // SkipLink (US-255) at the very top of the tree so it covers every route
  // — including onboarding and auth — without duplication, plus a
  // route-change focus reset hook (WCAG 2.4.3). See `RootShell` below.
  component: () => <RootShell />,
  // US-045 — unknown paths bounce to `/inbox` via a router-level redirect.
  // The auth probe in `beforeLoad` runs first so logged-out users still
  // hit `/auth/login`.
  notFoundComponent: () => {
    throw redirect({
      to: '/inbox',
      search: { accountId: undefined, mode: undefined, selectedId: undefined },
      replace: true,
    });
  },
});

// Re-exported for symmetry with the per-route convention.
export { ApiError };

/**
 * `RootShell` — global root component mounted by the router.
 *
 * Responsibilities:
 *  - Mount the canonical `<SkipLink>` (US-255) above every route. A single
 *    instance covers AppShell, OnboardingShell, and AuthLayout because they
 *    all render a `<main id="main-content">` landmark. AuthLayout keeps its
 *    own scoped `auth-skip-link` for layout-specific copy; both anchor
 *    to `#main-content` so there's no target conflict.
 *  - Reset keyboard focus to the main landmark on every route-change
 *    (WCAG 2.4.3). We skip the initial mount so the page-load focus
 *    resolution still belongs to the document.
 */
function RootShell() {
  const t = useT('shell');
  const router = useRouterState();
  const pathname = router.location.pathname;
  const initialMount = useRef(true);

  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }
    if (typeof document === 'undefined') return;
    const main = document.getElementById(APP_MAIN_LANDMARK_ID);
    if (!main) return;
    // Ensure the landmark is programmatically focusable. The DS `AppShell`
    // already sets `tabIndex={-1}` on its `<main>`, but other surfaces
    // (OnboardingShell, AuthLayout) may render their own — we coerce here
    // for parity rather than spreading the concern across templates.
    if (!main.hasAttribute('tabindex')) {
      main.setAttribute('tabindex', '-1');
    }
    main.focus({ preventScroll: true });
  }, [pathname]);

  return (
    <>
      <SkipLink label={t('skipLink.label')} />
      <Outlet />
    </>
  );
}
